import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { UsersUserResponse } from '../../../../../../../../../libs/shared/generated/src/lib/types/users';
import { MessageService } from 'primeng/api';
import { PreEmploymentActivitiesService } from '../../../../../services/pre-employment-activities.service';
import { CheckboxModule } from 'primeng/checkbox';
import { DividerModule } from 'primeng/divider';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { SelfserviceAcknowledgementActivityDetailResponse, SelfserviceAcknowledgementSubmission } from '../../../../../../../../../libs/shared/generated/src/lib/types/selfservice';
import { SkeletonModule } from 'primeng/skeleton';

function isAcknowledgementActivity(
  activity: unknown,
): activity is SelfserviceAcknowledgementActivityDetailResponse {
  return (activity as { kind?: string })?.kind === 'ACKNOWLEDGEMENT';
}

@Component({
  selector: 'app-pre-employment-acknowledgement-dialog',
  imports: [
    CommonModule,
    ButtonModule,
    DialogModule,
    TranslateModule,
    CheckboxModule,
    DividerModule,
    FormsModule,
    ReactiveFormsModule,
    SkeletonModule
  ],
  templateUrl: './pre-employment-acknowledgement-dialog.component.html',
  styleUrl: './pre-employment-acknowledgement-dialog.component.scss'
})
export class PreEmploymentAcknowledgementDialogComponent {


  @Input() open: boolean = false;
  @Input() me: UsersUserResponse | null = null
  @Output() close: EventEmitter<void> = new EventEmitter<void>();
  @Output() submitted: EventEmitter<void> = new EventEmitter<void>();
  @Input() activity: any;
  @Input() requestId?: string | null = null
  @Input() form: FormGroup;
  isLoading: boolean = true;
  isCompleted: boolean = false
  isSaving: boolean = false;
  notes: string = '';
  documentUrl: string | null = null;
  documentFileName: string | null = null

  constructor(
    private readonly translate: TranslateService,
    private readonly messageService: MessageService,
    private readonly preEmploymentActivitiesService: PreEmploymentActivitiesService,
    private readonly fb: FormBuilder
  ) { }


  ngOnInit(): void {
    this.form = this.fb.group({
      notesAcknowledged: [false, Validators.requiredTrue],
    });

    if (!this.requestId) {
      this.isLoading = false;
      return;
    }

    this.preEmploymentActivitiesService.getActivityDetail(this.requestId).subscribe({
      next: (response) => {
        const activity = response.activity;

        if (isAcknowledgementActivity(activity)) {
          this.notes = activity.notes;
          this.documentUrl = activity.document?.presignedUrl ?? null;
          this.documentFileName = activity.document?.fileName ?? null;
        }

        const submission = response.submission;
        if (submission?.activityKind === 'ACKNOWLEDGEMENT') {
          this.isCompleted = true;
          this.form.patchValue({ notesAcknowledged: true });
          this.form.disable();
        }
        this.isLoading = false;

      },
      error: () => {
        this.isLoading = false;
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('global.error.label'),
          detail: this.translate.instant('pre-employment.file-upload-dialog.generic.error.detail'),
        });
      },
    });
  }

  save(): void {
    if (!this.requestId || this.form.invalid) return;

    const payload: SelfserviceAcknowledgementSubmission = {
      activityKind: 'ACKNOWLEDGEMENT',
    };

    this.isSaving = true;

    this.preEmploymentActivitiesService.submitActivity(this.requestId, payload).subscribe({
      next: () => {
        this.isSaving = false;
        this.isCompleted = true;
        this.submitted.emit();
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('global.success.label'),
          detail: this.translate.instant('pre-employment-acknowledgement-dialog.toast-success-message'),
        });
        this.onClose();
      },
      error: () => {
        this.isSaving = false;
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('global.error.label'),
          detail: this.translate.instant('pre-employment-acknowledgement-dialog.toast-error-message'),
        });
      },
    });
  }

  onClose(): void {
    this.form.reset();
    this.close.emit();
  }
}
